import { AudioLines, Brush, Mountain, type LucideIcon } from 'lucide-react';
import { interests } from '@/lib/portfolio';
import { cn } from '@/lib/utils';
import { Reveal } from './Reveal';
import { SectionHeading } from './SectionHeading';

const iconMap: Record<string, LucideIcon> = {
  Mountain,
  Brush,
  AudioLines,
};

const tilts = ['md:-rotate-2', 'md:rotate-1', 'md:-rotate-1'];

function Equalizer() {
  return (
    <span className="flex h-5 items-end gap-1" aria-hidden="true">
      {[0, 1, 2, 3].map((bar) => (
        <span
          key={bar}
          className="h-full w-1 origin-bottom animate-eq rounded-full bg-flare"
          style={{ animationDelay: `${bar * 0.18}s` }}
        />
      ))}
    </span>
  );
}

export function Beyond() {
  return (
    <section id="play" className="relative scroll-mt-28 py-28 md:py-36">
      <div className="container">
        <SectionHeading
          eyebrow="Beyond the code"
          title={
            <>
              Touch grass,{' '}
              <span className="font-serif font-normal italic text-mint">then commit again.</span>
            </>
          }
          description="The human behind the deploys: trails, textures, and a build soundtrack with serious range."
        />

        <div className="mt-16 grid gap-6 md:grid-cols-3 md:gap-4">
          {interests.map((interest, i) => {
            const Icon = iconMap[interest.icon] ?? Mountain;
            const isMusic = interest.icon === 'AudioLines';
            return (
              <Reveal key={interest.title} delay={0.08 * i}>
                <div
                  className={cn(
                    'group h-full rounded-3xl border border-white/10 bg-card/60 p-8 backdrop-blur transition-all duration-500',
                    'hover:rotate-0 hover:scale-[1.03] hover:border-white/25 hover:bg-card',
                    tilts[i % tilts.length],
                  )}
                >
                  <div className="flex items-start justify-between">
                    <span className="flex size-14 items-center justify-center rounded-2xl border border-white/10 bg-white/5 transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-110">
                      <Icon className={cn('size-7', interest.accent)} />
                    </span>
                    {isMusic && <Equalizer />}
                  </div>

                  <p className={cn('mt-6 text-xs font-semibold uppercase tracking-[0.3em]', interest.accent)}>
                    {interest.kicker}
                  </p>
                  <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">
                    {interest.title}
                  </h3>
                  <p className="mt-3 leading-relaxed text-muted-foreground">{interest.body}</p>

                  <div className="mt-6 flex flex-wrap gap-2">
                    {interest.chips.map((chip) => (
                      <span
                        key={chip}
                        className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-muted-foreground"
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
